import moment from 'moment'
import { useContext, useMemo } from 'react'

import { Alert, Select, Switch } from 'antd'
import { useSelector } from 'react-redux'
import { faAt, faCalendar, faDollarSign, faStethoscope, faUser } from '@fortawesome/pro-solid-svg-icons'

import FormItem from '../../../Components/shared/Forms/FormItem'
import Mailing from '../../../Components/shared/Mailing'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import SectorsSelector from '../../../Components/shared/Selectors/SectorsSelector'
import { EUROPEAN_DATE, InstitutionOptions } from '../../../utils/constants'
import { getEntityOrNull, getIdOrNull, isObject } from '../../../utils'
import { selectUser } from '../../../reducers/UserReducer'
import useInstitutionSectors from '../../../utils/api/hooks/useInstitutionSectors'
import { isInstitutionGroupAdmin, isSchoolUser } from '../../../utils/roles'
import { getProposedPeriods } from '../../../utils/entities/internshipRequest'
import { InstitutionContext } from '../../InstitutionProvider'

const AcceptIndividualInternshipRequestForm = ({
  student, request, sector = null, compensation = false, proposal = null, agrees = false, selectedPeriods = [], onCompensationChange, onSectorSelect, onPeriodsSelect
}) => {
  const { options } = useContext(InstitutionContext) ?? {}

  const user = useSelector(selectUser)
  const t = useSelector(selectTranslate)
  const userIsInstitutionGroupAdmin = isInstitutionGroupAdmin(user)

  const sectorsList = useInstitutionSectors(request.institution)

  const proposedPeriods = useMemo(() => getProposedPeriods(request, proposal), [request, proposal])
  const proposedSectorName = useMemo(() => {
    const proposedSector = getIdOrNull(proposal?.sector)

    if (proposedSector === null) {
      return null
    }

    return sectorsList.find(s => s.id === proposedSector)?.name ??
      (isObject(proposal.sector) ? proposal.sector.name : null) ??
      (getIdOrNull(request?.sector) === proposedSector ? request?.sector?.name : null) ??
      null
  }, [proposal, request, sectorsList])

  const durationText = request.duration !== null && request.duration !== undefined ? `${request.duration} ${t('internship_request.duration.units.hour')}` : null
  const useInternshipCompensation = isObject(options) && options[InstitutionOptions.INTERNSHIP_COMPENSATION]
  const requestPeriodSummaries = useMemo(() => {
    if (!request?.requestPeriods?.length) {
      return []
    }

    return request.requestPeriods.map((period, index) => {
      if (!period?.startDate || !period?.endDate) {
        return null
      }

      const periodStart = moment(period.startDate).startOf('day')
      const periodEnd = moment(period.endDate).startOf('day')
      const start = periodStart.isAfter(periodEnd) ? periodEnd.clone() : periodStart.clone()
      const end = periodStart.isAfter(periodEnd) ? periodStart.clone() : periodEnd.clone()

      let totalDays = 0
      let weekDays = 0
      const currentDay = start.clone()

      while (currentDay.isSameOrBefore(end, 'day')) {
        totalDays += 1

        if (currentDay.isoWeekday() !== 6 && currentDay.isoWeekday() !== 7) {
          weekDays += 1
        }

        currentDay.add(1, 'day')
      }

      return {
        id: period.id ?? `${index}`,
        index: index + 1,
        totalDays,
        weekDays
      }
    }).filter(Boolean)
  }, [request?.requestPeriods])

  return (
    <div className='h-100 w-100 flex-column'>
      {proposal !== null && (
        <Alert
          className='mb-8'
          type={agrees ? 'info' : 'warning'}
          showIcon
          message={t(isSchoolUser(user)
            ? 'accept_internship_request_modal.partner_proposal.from_institution'
            : 'accept_internship_request_modal.partner_proposal.from_school'
          )}
          description={
            <div className='flex-column'>
              <div>
                {t('form.label.period')} : {proposedPeriods.length === 0
                  ? t('accept_internship_request_modal.partner_proposal.periods_gone')
                  : proposedPeriods.map(p => `${p.startDate.format(EUROPEAN_DATE)} - ${p.endDate.format(EUROPEAN_DATE)}`).join(' | ')}
              </div>
              <div>
                {t('form.label.sector')} : {proposedSectorName ?? t('common.unknown_sector')}
              </div>
              <div className='mt-8'>
                {t(agrees
                  ? 'accept_internship_request_modal.partner_proposal.help.agreeing'
                  : 'accept_internship_request_modal.partner_proposal.help.differing'
                )}
              </div>
            </div>
          }
        />
      )}
      <div className='flex-row w-100'>
        <FormItem className='w-50 pdr-4' icon={faUser} label='form.label.firstname'>
          <div className='pdl-8 fake-gray-input w-100'>
            {student.firstname}
          </div>
        </FormItem>
        <FormItem className='w-50 pdl-4' icon={faUser} label='form.label.lastname'>
          <div className='pdl-8 fake-gray-input w-100'>
            {student.lastname}
          </div>
        </FormItem>
      </div>
      <FormItem icon={faAt} label='form.label.email'>
        <div className='pdl-8 fake-gray-input w-100'>
          <Mailing email={student.email} />
        </div>
      </FormItem>
      <FormItem icon={faStethoscope} label='form.label.sector' required>
        <SectorsSelector
          style={{ width: '100%' }}
          values={sectorsList.length === 0 ? (sector?.name ?? null) : (getIdOrNull(sector) ?? null)}
          sectors={sectorsList}
          fallbackToContext={false}
          onChange={(value, option, selected) => onSectorSelect(getEntityOrNull(selected) ?? getEntityOrNull(value))}
          disabled={sectorsList.length === 0 || (isObject(request?.sector) && !userIsInstitutionGroupAdmin && proposal === null)}
        />
      </FormItem>
      <FormItem icon={faCalendar} label='form.label.period' required>
        <Select className='w-100' value={selectedPeriods} onChange={onPeriodsSelect} mode='multiple'>
          {request.requestPeriods.map(p => <Select.Option key={p.id} value={p.id}> {p.startDate.format(EUROPEAN_DATE) + ' - ' + p.endDate.format(EUROPEAN_DATE)} </Select.Option>)}
        </Select>
      </FormItem>
      {useInternshipCompensation && (
        <div className='form-item'>
          <FormItem icon={faDollarSign} label='accept_internship_request_modal.compensation'>
            <div className='flex-row jc-between ai-ctr w-100'>
              <div style={{ color: '#d1d1d1', fontSize: 14, fontWeight: 600, paddingLeft: 0, lineHeight: 1.2 }}>
                {t('accept_internship_request_modal.duration')} : {durationText}
              </div>
              <Switch checked={compensation} onChange={onCompensationChange} />
            </div>
            {requestPeriodSummaries.length > 0 && (
              <div style={{ color: '#d1d1d1', fontSize: 14, fontWeight: 600, lineHeight: 1.5, marginTop: 8 }}>
                {requestPeriodSummaries.map(period => (
                  <div key={period.id}>
                    <div>
                      P{period.index} : {t('accept_internship_request_modal.period_days')} : {period.totalDays} {t('internship_request.duration.units.day')}
                    </div>
                    <div style={{ paddingLeft: 32 }}>
                      {t('accept_internship_request_modal.period_weekdays')} : {period.weekDays} {t('internship_request.duration.units.day')}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </FormItem>
        </div>
      )}
    </div>
  )
}

export default AcceptIndividualInternshipRequestForm
